if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.minScale = 1;
        this.maxScale = 5;
        this.step = 0.4;
        this.scale = 1;
        this.translateX = 0;
        this.translateY = 0;
        this.isPanning = false;
        this.pinchStartDistance = 0;
        this.pinchStartScale = 1;

        this.onZoomClick = this.onZoomClick.bind(this);
        this.onWheel = this.onWheel.bind(this);
        this.onDoubleClick = this.onDoubleClick.bind(this);
        this.onPointerDown = this.onPointerDown.bind(this);
        this.onPointerMove = this.onPointerMove.bind(this);
        this.onPointerUp = this.onPointerUp.bind(this);
        this.onTouchStart = this.onTouchStart.bind(this);
        this.onTouchMove = this.onTouchMove.bind(this);
        this.onTouchEnd = this.onTouchEnd.bind(this);
      }

      get zoomEnabled() {
        return this.dataset.lightboxZoom === 'true';
      }

      connectedCallback() {
        super.connectedCallback?.();
        // ModalDialog moves this node to body; bind zoom after that.
        if (this.zoomEnabled && !this.zoomBound) {
          this.zoomBound = true;
          this.initLightboxZoom();
        }
      }

      initLightboxZoom() {
        this.addEventListener('click', this.onZoomClick);
        this.addEventListener('pointerup', (event) => {
          if (event.target.closest('[data-zoom], .product-media-modal__zoom-controls')) {
            event.stopPropagation();
          }
        });

        const content = this.querySelector('.product-media-modal__content');
        if (!content) return;

        content.addEventListener('wheel', this.onWheel, { passive: false });
        content.addEventListener('dblclick', this.onDoubleClick);
        content.addEventListener('pointerdown', this.onPointerDown);
        window.addEventListener('pointermove', this.onPointerMove);
        window.addEventListener('pointerup', this.onPointerUp);
        content.addEventListener('touchstart', this.onTouchStart, { passive: false });
        content.addEventListener('touchmove', this.onTouchMove, { passive: false });
        content.addEventListener('touchend', this.onTouchEnd);
        content.addEventListener('touchcancel', this.onTouchEnd);
      }

      onZoomClick(event) {
        if (!this.zoomEnabled) return;
        const btn = event.target.closest('[data-zoom]');
        if (!btn || !this.contains(btn)) return;
        event.preventDefault();
        event.stopPropagation();
        this.adjustZoom(btn.dataset.zoom === 'in' ? this.step : -this.step);
      }

      hide() {
        this.resetZoom();
        super.hide();
      }

      show(opener) {
        this.resetZoom();
        super.show(opener);
        this.showActiveMedia();
        // Re-apply after layout so the correct image is targeted
        requestAnimationFrame(() => this.resetZoom());
      }

      showActiveMedia() {
        const openerId = this.openedBy?.getAttribute('data-media-id');
        if (!openerId) return;

        this.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('active', element.getAttribute('data-media-id') === openerId);
        });

        const activeMedia = this.querySelector(`[data-media-id="${openerId}"]`);
        if (!activeMedia) return;

        const activeMediaTemplate = activeMedia.querySelector?.('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.scrollIntoView({ block: 'nearest' });

        const container = this.querySelector('[role="document"]');
        if (container && activeMedia.width) {
          container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;
        }

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        ) {
          activeMedia.loadContent();
        }
      }

      /**
       * Product images put data-media-id on the <img> itself.
       * Videos/models put it on a wrapper. Handle both.
       */
      getActiveImage() {
        const active =
          this.querySelector('[data-media-id].active') ||
          this.querySelector('.product-media-modal__content [data-media-id]');
        if (!active) return null;
        if (active.tagName === 'IMG') return active;
        return active.querySelector('img');
      }

      adjustZoom(delta, clientX, clientY) {
        const img = this.getActiveImage();
        if (!img) return;

        const next = Math.min(this.maxScale, Math.max(this.minScale, +(this.scale + delta).toFixed(2)));
        if (next === this.scale) {
          this.updateZoomUi();
          return;
        }

        if (next === this.minScale) {
          this.translateX = 0;
          this.translateY = 0;
        } else if (typeof clientX === 'number' && typeof clientY === 'number') {
          const rect = img.getBoundingClientRect();
          const ox = clientX - (rect.left + rect.width / 2);
          const oy = clientY - (rect.top + rect.height / 2);
          const ratio = next / this.scale;
          this.translateX = this.translateX * ratio + ox * (1 - ratio);
          this.translateY = this.translateY * ratio + oy * (1 - ratio);
        }

        this.scale = next;
        this.applyZoom();
      }

      applyZoom() {
        const img = this.getActiveImage();
        if (!img) return;

        img.style.setProperty(
          'transform',
          `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`,
          'important'
        );
        img.style.setProperty('transform-origin', 'center center', 'important');
        img.classList.toggle('is-zoomed', this.scale > 1);
        this.classList.toggle('is-zoomed', this.scale > 1);
        this.updateZoomUi();
      }

      updateZoomUi() {
        const zoomIn = this.querySelector('[data-zoom="in"]');
        const zoomOut = this.querySelector('[data-zoom="out"]');
        if (zoomIn) zoomIn.disabled = this.scale >= this.maxScale - 0.001;
        if (zoomOut) zoomOut.disabled = this.scale <= this.minScale + 0.001;
      }

      resetZoom() {
        this.scale = 1;
        this.translateX = 0;
        this.translateY = 0;
        this.isPanning = false;
        this.pinchStartDistance = 0;
        this.querySelectorAll('.product-media-modal__content img').forEach((img) => {
          img.style.removeProperty('transform');
          img.style.removeProperty('transform-origin');
          img.classList.remove('is-zoomed');
        });
        this.classList.remove('is-zoomed');
        this.updateZoomUi();
      }

      onWheel(event) {
        if (!this.zoomEnabled || !this.hasAttribute('open')) return;
        event.preventDefault();
        event.stopPropagation();
        const delta = event.deltaY < 0 ? this.step : -this.step;
        this.adjustZoom(delta, event.clientX, event.clientY);
      }

      onDoubleClick(event) {
        if (!this.zoomEnabled) return;
        if (!event.target.closest('img')) return;
        event.preventDefault();
        event.stopPropagation();
        if (this.scale > 1) {
          this.resetZoom();
        } else {
          this.adjustZoom(1.2, event.clientX, event.clientY);
        }
      }

      onPointerDown(event) {
        if (!this.zoomEnabled) return;
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (!event.target.closest('img')) return;
        if (this.scale <= 1) return;

        this.isPanning = true;
        this.panStartX = event.clientX - this.translateX;
        this.panStartY = event.clientY - this.translateY;
        try {
          event.target.setPointerCapture?.(event.pointerId);
        } catch (e) {}
        event.preventDefault();
        event.stopPropagation();
      }

      onPointerMove(event) {
        if (!this.isPanning || this.scale <= 1) return;
        this.translateX = event.clientX - this.panStartX;
        this.translateY = event.clientY - this.panStartY;
        this.applyZoom();
      }

      onPointerUp() {
        this.isPanning = false;
      }

      getTouchDistance(touches) {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.hypot(dx, dy);
      }

      onTouchStart(event) {
        if (!this.zoomEnabled) return;
        if (event.touches.length === 2) {
          event.preventDefault();
          this.pinchStartDistance = this.getTouchDistance(event.touches);
          this.pinchStartScale = this.scale;
        }
      }

      onTouchMove(event) {
        if (!this.zoomEnabled) return;
        if (event.touches.length === 2 && this.pinchStartDistance) {
          event.preventDefault();
          const distance = this.getTouchDistance(event.touches);
          const next = Math.min(
            this.maxScale,
            Math.max(this.minScale, this.pinchStartScale * (distance / this.pinchStartDistance))
          );
          const midX = (event.touches[0].clientX + event.touches[1].clientX) / 2;
          const midY = (event.touches[0].clientY + event.touches[1].clientY) / 2;
          this.adjustZoom(next - this.scale, midX, midY);
        }
      }

      onTouchEnd(event) {
        if (event.touches.length < 2) {
          this.pinchStartDistance = 0;
        }
      }
    }
  );
}
